// v2 leaping fish — a salmon-built leaper: a fusiform body with an arched back,
// a wedge head with a gill plate, an iridescent lateral stripe down each flank,
// a swept-back dorsal fin, paired pectoral fins, and a narrow tail stock flaring
// into a genuinely forked two-blade tail. Two variants: v0 silver-blue sea fish,
// v1 rosy trout. Ported 1:1 from age-of-mcp's v2/leaping-fish.ts, using the
// library's shared `wetland` (eyes) helper.
import * as THREE from "three";
import { defineModel, wetland, type Part } from "../../lib";

const SCALES = [
  { back: "#7e96a8", flank: "#a8bcc8", belly: "#d8e2e8", fin: "#68808f",
    stripe: "#c8d8e2", gill: "#8ea4b2" },
  { back: "#6e8262", flank: "#a8b49a", belly: "#e2e0d2", fin: "#5a6c52",
    stripe: "#d88a8a", gill: "#92a088" },
];

function leapingFishParts(v: number): Part[] {
  const c = SCALES[v % SCALES.length]!;
  return [
    // fusiform body with an arched back line
    { geometry: new THREE.IcosahedronGeometry(0.032, 0), color: c.flank,
      position: [0.01, 0.028, 0], scale: [1.9, 0.8, 0.55] },
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: c.back,
      position: [0.0, 0.038, 0], scale: [1.9, 0.6, 0.5] },
    { geometry: new THREE.IcosahedronGeometry(0.022, 0), color: c.belly,
      position: [0.012, 0.018, 0], scale: [1.7, 0.65, 0.5] },
    // iridescent lateral stripe down each flank
    { geometry: new THREE.BoxGeometry(0.075, 0.005, 0.002), color: c.stripe,
      position: [0.005, 0.029, 0.0095] },
    { geometry: new THREE.BoxGeometry(0.075, 0.005, 0.002), color: c.stripe,
      position: [0.005, 0.029, -0.0095] },
    // wedge head and gill plate
    { geometry: new THREE.ConeGeometry(0.02, 0.035, 4), color: c.flank,
      position: [0.075, 0.027, 0], rotation: [0, 0, -Math.PI / 2],
      scale: [0.75, 1, 0.55] },
    { geometry: new THREE.IcosahedronGeometry(0.013, 0), color: c.gill,
      position: [0.052, 0.028, 0], scale: [0.35, 0.95, 0.62] },
    // swept-back dorsal fin
    { geometry: new THREE.ConeGeometry(0.011, 0.026, 3), color: c.fin,
      position: [0.005, 0.055, 0], rotation: [0, 0, 0.45], scale: [1, 1, 0.3] },
    // pectoral fins angled back-down
    { geometry: new THREE.ConeGeometry(0.006, 0.018, 3), color: c.fin,
      position: [0.045, 0.018, 0.013], rotation: [0, 0.6, 2.3], scale: [0.4, 1, 1] },
    { geometry: new THREE.ConeGeometry(0.006, 0.018, 3), color: c.fin,
      position: [0.045, 0.018, -0.013], rotation: [0, -0.6, 2.3], scale: [0.4, 1, 1] },
    // narrow tail stock flaring into a forked tail
    { geometry: new THREE.CylinderGeometry(0.006, 0.011, 0.025, 4), color: c.flank,
      position: [-0.058, 0.028, 0], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.ConeGeometry(0.009, 0.032, 3), color: c.fin,
      position: [-0.082, 0.038, 0], rotation: [0, 0, Math.PI / 2 - 0.5],
      scale: [1, 1, 0.25] },
    { geometry: new THREE.ConeGeometry(0.009, 0.032, 3), color: c.fin,
      position: [-0.082, 0.018, 0], rotation: [0, 0, Math.PI / 2 + 0.5],
      scale: [1, 1, 0.25] },
    ...wetland.eyes(0.082, 0.032, 0.009, 0.0035),
  ];
}

export const leapingFishV2 = defineModel({
  id: "leaping-fish-v2",
  batchId: "fable",
  name: "Leaping fish (v2)",
  tags: ["creature","leaping","fish","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly leaping fish.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.08,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => leapingFishParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => leapingFishParts(1) },
  ],
  animations: [],
});
